import { format, parseISO, isValid, formatDistanceToNow } from 'date-fns';
import { enUS, ptBR, pt, de, pl } from 'date-fns/locale';

const LOCALE_MAP = {
  en: enUS,
  'pt-BR': ptBR,
  'pt-PT': pt,
  de,
  pl,
};

/**
 * Get the date-fns locale for an i18n language code.
 * Falls back to the base language (e.g. "pt" -> pt-BR, "en-US" -> en), then enUS.
 */
export function getDateLocale(language: string = 'en') {
  const exact = LOCALE_MAP[language as keyof typeof LOCALE_MAP];
  if (exact) return exact;

  const base = language.split('-')[0];
  const key = (Object.keys(LOCALE_MAP) as Array<keyof typeof LOCALE_MAP>).find(
    (k) => k.split('-')[0] === base,
  );
  return key ? LOCALE_MAP[key] : enUS;
}

/**
 * Format a date. When `language` is omitted, the date-fns default locale is
 * used — it is kept in sync with the active UI language in i18n/config.ts.
 */
export function formatDate(
  date: Date | string,
  formatString: string = 'PPP',
  language?: string,
): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;

  if (!isValid(dateObj)) {
    return '';
  }

  return format(
    dateObj,
    formatString,
    language ? { locale: getDateLocale(language) } : undefined,
  );
}

/**
 * Format time with localization
 */
export function formatTime(
  date: Date | string,
  formatString: string = 'p',
  language?: string,
): string {
  return formatDate(date, formatString, language);
}

/**
 * Format date and time
 */
export function formatDateTime(
  date: Date | string,
  formatString: string = 'PPp',
  language?: string,
): string {
  return formatDate(date, formatString, language);
}

/**
 * Format relative time (e.g., "2 minutes ago"). Uses the date-fns default locale.
 */
export function formatRelativeTime(date: string | Date | null): string {
  if (!date) return 'Never';
  return formatDistanceToNow(new Date(date), { addSuffix: true });
}
